import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "app/popleads/components/ui-kit/colors";
import RichTextEditor from "app/popleads/components/ui-kit/RichTextEditor";
import useProfileNoteActions from "app/popleads/hooks/useProfileNoteActions";
import { useExtensionContext } from "../ExtensionContext";
import { ExtensionBody, ExtensionSmall } from "app/popleads/components/ui-kit/Text";
import { useDropzone } from "react-dropzone";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { decideFileType } from "../../activities/utils";
import useGetProcessMimeMessage from "app/popleads/hooks/useGetProcessMimeMessage";
import highlightFromTo from "../utils/highlightFromTo";
import NotesFileCard, { Container, FileDetails, FileName } from "app/popleads/components/ui-kit/NotesFileCard";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { ProfileLoadingOverlay } from "../ExtensionHome";
import { StyledCardFooter, StyledCardHeader } from "./common/styled";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { toast } from "react-toastify";
import moment from "moment";
import { useSelector } from "react-redux";
import Modal from "app/popleads/components/ui-kit/Modal";

const EditNoteButtonContainer = styled.div`
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: end;
`;
export const StyledNoteContent = styled.div`
    color: ${({ variant }) => (variant === "warning" ? colors?.warning[800] : colors.neutral[800])};
    font-size: 14px;
    font-weight: 400;
    letter-spacing: -0.17px;
    line-height: 21px;
    overflow-wrap: anywhere;
    ${({ variant }) => variant === "notEdit" && "cursor: pointer;"}
    ${({ isExpanded }) =>
        !isExpanded &&
        `
    display: -webkit-box;
    -webkit-line-clamp: 3;  
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
    & > p {
        margin: 0;
    }

    & i {
        color: inherit;
        font-size: inherit;
        font-weight: inherit;
        letter-spacing: inherit;
        line-height: inherit;
    }
`;

const StyledContentWrapper = styled.div``;

const StyledDragAndDrop = styled.div`
    padding: 8px 16px;
    font-size: 14px;
    background-color: ${colors.neutral[25]};
    border-width: 1px;
    border-style: dashed;
    border-color: ${({ isDragActive }) => (isDragActive ? colors.primary[900] : colors.neutral[300])};
    border-radius: 8px;
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 16px;
`;

const FileItem = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
    margin-top: 12px;
    & > span {
        color: #8b999f;
        font-size: 12px;
        font-weight: 400;
        line-height: 20px;
    }
`;

const StyledMailButton = styled("div")`
    font-size: 12px;
    font-weight: 400;
    color: ${colors.primary[900]};
    cursor: pointer;
`;

export const ShowMoreButton = styled.button`
    background: none;
    border: none;
    color: ${colors.primary[900]};
    cursor: pointer;
    padding: 4px 0;
    font-size: 14px;
    font-weight: 500;
    margin-top: 8px;
    display: block;
`;

const ExtensionNote = ({ note }) => {
    const {
        profile,
        openMailModal,
        profileNotesRefetch,
        companyUsers,
        refetchProfile,
        handleDeleteNote,
    } = useExtensionContext();
    const { updateNote, loading: noteLoading } = useProfileNoteActions();
    const [isEditing, setIsEditing] = useState(false);
    const [newNote, setNewNote] = useState(note.metin);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const wrapperRef = useRef(null);
    const { loading: processingMail, refetch: processMimeMessage } = useGetProcessMimeMessage(note?.mailId);
    const formRef = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);

    const { user } = useSelector((state) => state.auth);
    const now = new Date();
    const fifteenMinutesInMillisecond = 15 * 60 * 1000;
    const fifteenMinutesAgo = now.getTime() - fifteenMinutesInMillisecond;
    const noteEditOrDeletePermission =
        user.id === note?.kayitciId && note?.zaman > fifteenMinutesAgo && note?.tip !== "otomatik";

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        noClick: true,
        noKeyboard: true,
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "text/plain": [".txt"],
            "video/mp4": [".mp4"],
        },
        onDrop: (acceptedFiles) => {
            setSelectedFiles((prevFiles) =>
                [...prevFiles, ...acceptedFiles].map((file) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    })
                )
            );
        },
        onDropRejected: () => {
            toast.error("Please upload only DOC, XLS, TXT, PDF,MP4, JPEG or PNG files");
        },
    });

    function uploadPlugin(editor) {
        editor.editing.view.document.on("clipboardInput", (event, data) => {
            const files = Array.from(data.dataTransfer.files).filter(
                (file) =>
                    file.type.startsWith("image/jpeg") ||
                    file.type.startsWith("image/png") ||
                    file.type.startsWith("application/pdf") ||
                    file.type.startsWith("application/msword") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
                    file.type.startsWith("application/vnd.ms-excel") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
                    file.type.startsWith("text/plain") ||
                    file.type.startsWith("video/mp4")
            );
            if (files.length !== data.dataTransfer.files.length) {
                toast.error("Please upload only DOC, XLS, TXT, PDF,MP4, JPEG or PNG files");
            }
            if (files.length > 0) {
                event.stop();
                setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
            }
        });
    }

    const mentionsFeed = companyUsers?.map((user) => {
        return {
            userId: user.id,
            text: user.name || user.ad,
            id: `@${user.name || user.ad}`,
        };
    });

    const editorConfig = {
        toolbar: ["bold", "italic", "bulletedList", "numberedList"],
        placeholder: "Type your note *Activate mention by typing '@'",
        extraPlugins: [uploadPlugin],
        startupFocus: true,
        mention: {
            feeds: [
                {
                    marker: "@",
                    feed: mentionsFeed || [],
                    minimumCharacters: 1,
                },
            ],
        },
        link: {
            addTargetToExternalLinks: true,
        },
    };

    const handleSubmit = async () => {
        let sendNoteObj = {
            profil: profile.id,
            note: note.id,
            text: newNote,
        };

        if (selectedFiles && selectedFiles.length > 0) {
            const inputnames = [];
            selectedFiles.forEach((file, index) => {
                if (file && file.name) {
                    inputnames.push(`note${index}`);
                    sendNoteObj[`note${index}`] = file;
                }
            });
            if (inputnames.length > 0) {
                sendNoteObj.inputName = inputnames;
            }
        }

        const result = await updateNote(sendNoteObj);

        if (result?.error) {
            setNewNote(note.metin);
            // return;
        } else if (result?.data) {
            profileNotesRefetch({ rowsPerPage: 100 });
            if (selectedFiles.length > 0) refetchProfile();
        }

        setSelectedFiles([]);
        // setNote("");
        // addProfileNote(result.data.not);
    };

    useEffect(() => {
        if (note.metin !== newNote) {
            setNewNote(note.metin);
        }
    }, [note.metin]);

    const showMailContent = async () => {
        const content = await processMimeMessage();
        const newMailContent = document.createElement("div");
        newMailContent.innerHTML = content.replace("img/down.png", "/media/svg/popleads/download-cloud.svg");
        newMailContent.querySelectorAll("a").forEach((link) => {
            link.setAttribute("target", "_blank");
            link.setAttribute("rel", "noopener noreferrer");
        });
        if (newMailContent) {
            openMailModal(newMailContent);
        }
    };
    const handleEditorReady = (editor) => {
        // Set the initial selection to the end of the document
        editor.model.change((writer) => {
            const endPosition = writer.createPositionAt(editor.model.document.getRoot(), "end");
            writer.setSelection(endPosition);
        });
        editor.editing.view.focus();

        editor.editing.view.document.on("keydown", (event, data) => {
            if (data.keyCode === 13) {
                if (!data.shiftKey) {
                    data.preventDefault();
                    if (formRef.current) {
                        formRef.current.dispatchEvent(new Event("submit", { cancelable: true }));
                        setIsEditing(false);
                    }
                } else {
                    const selection = editor.model.document.selection;
                    const currentPosition = selection.getLastPosition();
                    const currentBlock = currentPosition.parent;

                    if (currentBlock.name === "listItem" && currentBlock.isEmpty) {
                        editor.model.change((writer) => {
                            const paragraph = writer.createElement("paragraph");
                            writer.insert(paragraph, currentBlock, "after");
                            ///move cursor to new paragraph
                            writer.setSelection(paragraph, "in");

                            data.domEvent.preventDefault();
                        });
                        // Scroll to the bottom of the editor
                        const editable = editor.ui.view.editable.element;
                        editable.scrollTop = editable.scrollHeight;
                        return;
                    }
                    if (
                        editor.model.document.selection.anchor.parent.name === "listItem" ||
                        editor.model.document.selection.anchor.parent.name === "paragraph"
                    ) {
                        editor.execute("enter");
                    }
                    // Scroll to the bottom of the editor
                    const editable = editor.ui.view.editable.element;
                    editable.scrollTop = editable.scrollHeight;
                    data.domEvent.preventDefault();
                }
            }
        });
    };

    const countLines = (text) => {
        if (!text) return 0;

        const isHTML = /<\/?[a-z][\s\S]*>/i.test(text);

        if (isHTML) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, "text/html");
            const mentions = doc.querySelectorAll("span.mention");

            if (mentions.length > 0) {
                let totalLength = 0;
                mentions.forEach((el) => {
                    totalLength += el.textContent.trim().length;
                });
                return totalLength;
            } else {
                const allText = doc.body.textContent || "";
                const length = allText.trim().length;
                return length;
            }
        } else {
            const length = text.trim().length;
            return length;
        }
    };

    const shouldShowMoreButton = countLines(note.metin) > 128;

    return (
        <>
            <div className="d-flex justify-content-between align-items-center">
                <StyledCardHeader>{moment(note?.zaman).format("MMM DD, YYYY HH:mm")}</StyledCardHeader>
                {noteEditOrDeletePermission && !isEditing && (
                    <ButtonWithText
                        icon="trash-2"
                        iconType="feather"
                        size="circle"
                        variant="danger"
                        buttonStyle="borderless"
                        onClick={() => {
                            setModalOpen(true);
                        }}
                    />
                )}
            </div>
            {!isEditing ? (
                <div>
                    {noteLoading && (
                        <ProfileLoadingOverlay>
                            <Spinner animation="border" variant="primary" />
                        </ProfileLoadingOverlay>
                    )}

                    <StyledNoteContent
                        variant={note.tip === "otomatik" ? "warning" : noteEditOrDeletePermission ? "notEdit" : ""}
                        dangerouslySetInnerHTML={{
                            __html:
                                note.tip === "otomatik"
                                    ? highlightFromTo(note.metin)
                                    : note?.mailId && note.metin === ""
                                    ? "No Subject"
                                    : note.metin,
                        }}
                        onClick={() => {
                            if (note.tip === "otomatik") return;
                            if (noteEditOrDeletePermission) {
                                setIsEditing(true);
                            }
                        }}
                        isExpanded={isExpanded}
                    />
                    {shouldShowMoreButton && (
                        <ShowMoreButton onClick={() => setIsExpanded(!isExpanded)}>
                            {isExpanded ? "Show Less" : "Show More"}
                        </ShowMoreButton>
                    )}
                    <StyledCardFooter>Created by {note.kayitci}</StyledCardFooter>
                    <NotesFileCard attachments={note?.attachments} />

                    {note?.mailId && (
                        <StyledMailButton
                            onClick={showMailContent}
                            style={{ cursor: "pointer" }}
                            disabled={processingMail}
                        >
                            Show Mail
                        </StyledMailButton>
                    )}
                </div>
            ) : (
                isEditing && (
                    <StyledContentWrapper ref={wrapperRef}>
                        <form ref={formRef} onSubmit={handleSubmit}>
                            <RichTextEditor
                                minHeight={"40px"}
                                maxHeight={"130px"}
                                data={newNote}
                                config={editorConfig}
                                onChange={(event, editor) => {
                                    const editorData = editor.getData();
                                    setNewNote(editorData);
                                }}
                                onReady={(editor) => {
                                    try {
                                        handleEditorReady(editor);
                                    } catch (error) {}
                                }}
                            />
                        </form>
                        <EditNoteButtonContainer>
                            <ButtonWithText
                                onClick={() => {
                                    setNewNote(note.metin);
                                    setSelectedFiles([]);
                                    setIsEditing(false);
                                }}
                                variant="danger"
                                buttonStyle="borderless"
                                size="small"
                            >
                                Cancel
                            </ButtonWithText>
                            <ButtonWithText
                                variant="primary"
                                buttonStyle="default"
                                type="default"
                                size="small"
                                onClick={async () => {
                                    handleSubmit();
                                    setIsEditing(false);
                                }}
                            >
                                Save
                            </ButtonWithText>
                        </EditNoteButtonContainer>

                        <NotesFileCard isEditing={isEditing} attachments={note?.attachments} />
                        {selectedFiles &&
                            selectedFiles.length > 0 &&
                            selectedFiles.map((file, index) => (
                                <FileItem key={index}>
                                    <Container>
                                        <FileDetails>
                                            <SVG src={toAbsoluteUrl(decideFileType(file?.name))} />
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id={`tooltip-${index}`}>{file.name}</Tooltip>}
                                            >
                                                <FileName>{file.name}</FileName>
                                            </OverlayTrigger>
                                        </FileDetails>
                                        <span
                                            className="material-symbols-outlined"
                                            style={{ fontSize: "20px", cursor: "pointer" }}
                                            onClick={() => {
                                                setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                                            }}
                                        >
                                            close
                                        </span>
                                    </Container>
                                </FileItem>
                            ))}
                        <StyledDragAndDrop {...getRootProps()} isDragActive={isDragActive}>
                            <input {...getInputProps()} />
                            <span className="material-symbols-outlined">cloud_upload</span>
                            <div>
                                <div>
                                    <ExtensionBody>Drag and Drop files here or</ExtensionBody>{" "}
                                    <ExtensionBody
                                        style={{ color: "#0085BE", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={open}
                                    >
                                        browse computer
                                    </ExtensionBody>
                                </div>
                                <ExtensionSmall style={{ color: "#53676E" }}>
                                    File supported: DOC, XLS, TXT, PDF, JPEG, PNG
                                </ExtensionSmall>
                            </div>
                        </StyledDragAndDrop>
                    </StyledContentWrapper>
                )
            )}
            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Are you sure you want to delete this note?"
                variant="danger"
                icon="delete"
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onSuccess={() => {
                    handleDeleteNote(note.id);
                    setModalOpen(false);
                }}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </>
    );
};

export default ExtensionNote;
